import { createFileRoute } from "@tanstack/react-router";
import { Cookie, FormInput, MousePointerClick } from "lucide-react";

import { Reveal } from "@/components/site/Reveal";
import { Section } from "@/components/site/Section";
import { useT } from "@/lib/i18n-dict";
import { alternateLinks } from "@/lib/i18n";
import { localeMeta } from "@/lib/seo-head";

const TITLE = "Privacy & cookies — factory.online";
const DESCRIPTION =
  "What factory.online collects, who receives it, and how you control advertising cookies.";

export const Route = createFileRoute("/{-$locale}/privacy")({
  staticData: { sitemap: true },
  head: ({ params }) => {
    const __m = localeMeta(params, TITLE, DESCRIPTION);
    return {
      meta: [
        { title: __m.title },
        { name: "description", content: __m.description },
        { property: "og:title", content: __m.title },
        { property: "og:description", content: __m.description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: alternateLinks("/privacy"),
    };
  },
  component: PrivacyPage,
});

function PrivacyPage() {
  const t = useT();
  const sections = [
    {
      icon: FormInput,
      title: t("Contact and request forms"),
      body: t(
        "When you send us a request, we store the details you provide (name, work email, company, phone, country, industry and message) so our team can respond. We do not sell this data.",
      ),
    },
    {
      icon: MousePointerClick,
      title: t("Advertising measurement (Meta Pixel)"),
      body: t(
        "With your consent, this site uses the Meta Pixel from Meta Platforms, Inc. It reports page views and actions such as submitting a request form to Meta for measurement and ad optimization. Where the law requires consent, the pixel stays off until you accept.",
      ),
    },
    {
      icon: Cookie,
      title: t("Your choice"),
      body: t(
        "You can accept or withdraw advertising cookies at any time via Cookie settings in the footer. Your choice is saved on this device and applies immediately.",
      ),
    },
  ];

  return (
    <>
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-96"
          style={{ background: "var(--gradient-edge)" }}
        />
        <div className="container-page relative py-20 md:py-28">
          <Reveal className="max-w-2xl">
            <h1 className="font-display text-4xl leading-[1.05] font-bold text-balance-tight sm:text-5xl">
              {t("Privacy & cookies")}
            </h1>
            <p className="mt-6 text-base text-muted-foreground md:text-lg">
              {t("What we collect, who receives it, and how you control it.")}
            </p>
          </Reveal>
        </div>
      </section>

      <Section>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {sections.map((section, index) => (
            <Reveal
              key={section.title}
              delay={index * 60}
              className="flex h-full flex-col rounded-xl border border-border bg-surface/40 p-6"
            >
              <div className="flex size-11 items-center justify-center rounded-full border border-primary/25 bg-primary/10 text-primary">
                <section.icon className="size-5" />
              </div>
              <h2 className="mt-4 font-display text-lg font-semibold text-balance-tight">
                {section.title}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {section.body}
              </p>
            </Reveal>
          ))}
        </div>
      </Section>
    </>
  );
}
